/**
 * useUserAuth Hook
 * 
 * Custom hook for handling user authentication via midway SSO.
 * Manages authentication flow and provides fallback for localhost development.
 */

import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useUserContext } from '../contexts/user-context';

interface MidwayTokenPayload {
  sub?: string;
  [key: string]: any;
}

const getLocalUserAlias = (): string => {
  // for localhost development, return a default user alias
  return "Dev User";
};

const generateAvatarUrl = (userAlias: string): string | null => {
  if (!userAlias || location.hostname === "localhost") {
    // For localhost, return null to use fallback
    return null;
  }
  return `https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${userAlias}`;
};

export const useUserAuth = () => {
  const { state, dispatch } = useUserContext();

  useEffect(() => {
    const authenticateUser = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        if (location.hostname === "localhost") {
          // For localhost development, use fallback user
          const localUser = getLocalUserAlias();
          dispatch({ type: 'SET_USER_ALIAS', payload: localUser });
          dispatch({ type: 'SET_AVATAR_URL', payload: null }); // No avatar for localhost
          return;
        }

        // Production midway SSO authentication
        const clientId = location.origin;
        const midwaySSOURL = `https://midway-auth.amazon.com/SSO?scope=openid&response_type=id_token&response_mode=query&nonce=nonce&sentry_handler_version=AmazonLambdaInterceptorAuthorizer-1.0&client_id=${clientId}%3A443&redirect_uri=${clientId}`;
        
        const response = await fetch(midwaySSOURL, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
        }

        const tokenText = await response.text();
        const decoded: MidwayTokenPayload = jwtDecode(tokenText);
        
        const userAlias = decoded?.sub || "Unknown User";
        const avatarUrl = generateAvatarUrl(userAlias);
        
        dispatch({ type: 'SET_USER_ALIAS', payload: userAlias });
        dispatch({ type: 'SET_AVATAR_URL', payload: avatarUrl });

      } catch (error) {
        console.error('Authentication error:', error);
        dispatch({ 
          type: 'SET_ERROR', 
          payload: error instanceof Error ? error.message : 'Authentication failed'
        });
        
        // Fallback to a default user on error
        dispatch({ type: 'SET_USER_ALIAS', payload: 'User' });
        dispatch({ type: 'SET_AVATAR_URL', payload: null });
      }
    };

    // Only authenticate if we don't already have a user alias
    if (!state.userAlias && !state.isLoading) {
      authenticateUser();
    }
  }, [dispatch, state.userAlias, state.isLoading]);

  return {
    userAlias: state.userAlias,
    avatarUrl: state.avatarUrl,
    isLoading: state.isLoading,
    error: state.error,
    clearError: () => dispatch({ type: 'CLEAR_ERROR' })
  };
};
